import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import FilterIconSvg from "../../assets/image/filter.svg";
import { parseApiDate, toApiDate } from "../../utils/dateRange";

export const SingleDatePanel = ({
  selected,
  onChange,
  minDate,
  maxDate,
  disabled = false,
}) => (
  <DatePicker
    selected={selected}
    onChange={onChange}
    inline
    showMonthDropdown
    showYearDropdown
    dropdownMode="select"
    minDate={minDate}
    maxDate={maxDate}
    disabled={disabled}
  />
);

const formatButtonLabel = (value) => {
  const d = parseApiDate(value);
  if (!d) return "";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const SingleDatePicker = ({
  variant = "field",
  value = "",
  onChange,
  minDate,
  maxDate,
  disabled = false,
  label = "",
  placeholder = "Select date",
  align = "left",
  wrapperClassName = "",
  buttonClassName = "",
  className = "",
  closeOnSelect = true,
  onClear,
  error = "",
  filterIcon = FilterIconSvg,
  triggerIcon = null,
  id = "",
}) => {
  const ref = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const selectedDate = parseApiDate(value);
  const min = minDate ? (minDate instanceof Date ? minDate : parseApiDate(minDate)) : undefined;
  const max = maxDate ? (maxDate instanceof Date ? maxDate : parseApiDate(maxDate)) : undefined;

  useEffect(() => {
    if (variant === "panel") return undefined;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [variant]);

  const handleSelect = (date) => {
    if (!date) return;
    onChange?.(toApiDate(date));
    if (closeOnSelect) {
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    onChange?.("");
    onClear?.();
    setIsOpen(false);
  };

  if (variant === "panel") {
    return (
      <SingleDatePanel
        selected={selectedDate}
        onChange={handleSelect}
        minDate={min}
        maxDate={max}
        disabled={disabled}
      />
    );
  }

  const popoverAlign = align === "left" ? "left-0" : "right-0";

  const calendarDropdown = (
    <div
      className={`absolute ${popoverAlign} z-50 mt-2 rounded-lg bg-white p-3 shadow-lg`}
    >
      <SingleDatePanel
        selected={selectedDate}
        onChange={handleSelect}
        minDate={min}
        maxDate={max}
        disabled={disabled}
      />
      {value && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <button
            type="button"
            className="btn w-full !py-2 text-sm border border-gray-300 bg-white text-black hover:bg-gray-50"
            onClick={handleClear}
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );

  const display = formatButtonLabel(value) || placeholder;

  return (
    <div className={`relative ${className}`} ref={ref}>
      {label ? (
        <label className="mb-1 block text-sm font-UbuntuMedium text-gray-700">{label}</label>
      ) : null}
      <button
        id={id || undefined}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={
          buttonClassName ||
          `flex h-[44px] w-full min-w-[10rem] items-center justify-between rounded-lg border bg-white px-3 text-sm outline-none focus:border-[#0B3E64] ${
            disabled ? "cursor-not-allowed bg-slate-100 opacity-70" : ""
          } ${error ? "border-red-400" : "border-[#0B3E64]/40"}`
        }
      >
        <span className={value ? "text-gray-800" : "text-gray-500"}>{display}</span>
        {triggerIcon || (
          <img
            src={filterIcon}
            alt=""
            className="ml-2 h-4 w-4 shrink-0 invert grayscale brightness-100"
          />
        )}
      </button>
      {isOpen && !disabled && calendarDropdown}
      {error ? <p className="mb-0 mt-1 text-xs text-red-500">{error}</p> : null}
    </div>
  );
};

export default SingleDatePicker;
